import { useState } from "react";

import { formatDate } from "./format";
import type { ActiveExpiryReminder } from "./types";

// Phase 4d rental-expiry-reminders — FR-7. Banner with `Extend rental`
// and `Schedule pickup` CTAs whenever the order is inside its
// tier-relevant reminder window. Dismissable client-side; the server
// emits the same `active_expiry_reminder` shape on every page load,
// so when the rental enters the next reminder window the banner
// reappears (matching the slice doc's "reappears at the next
// reminder's anchor" behavior).
export function ExpiryReminderBanner({ orderNumber, reminder }:
                                     { orderNumber: string; reminder: ActiveExpiryReminder }): JSX.Element | null {
  // Dismiss-state lives in component state — the server doesn't track
  // dismissals. Reload (or a new reminder window) brings the banner
  // back; that matches FR-7's "dismissible but reappears at the next
  // reminder's anchor" semantics in the simplest possible way.
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      role="status"
      className="rounded border border-amber-300 bg-amber-50 p-3"
      data-testid="expiry-reminder-banner"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-xl leading-none">⏰</span>
        <div className="flex-1">
          <p className="font-medium text-amber-900">
            {headline(reminder)} <span className="text-amber-700">({formatDate(reminder.pickup_date)})</span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href={`/account/orders/${orderNumber}/extend`}
              className="inline-flex items-center justify-center rounded border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
            >
              Extend rental
            </a>
            <a
              href={`/account/orders/${orderNumber}/pickup`}
              className="inline-flex items-center justify-center rounded border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
            >
              Schedule pickup
            </a>
          </div>
        </div>
        <button
          type="button"
          aria-label="Hide expiry reminder"
          className="text-amber-700 hover:text-amber-900"
          onClick={() => setDismissed(true)}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function headline(r: ActiveExpiryReminder): string {
  if (r.days_until === 0) return "Your rental ends today";
  if (r.days_until === 1) return "Your rental ends tomorrow";
  return `Your rental ends in ${r.days_until} days`;
}
